import React from 'react';
import { Award, Users, Heart, Target, ShieldCheck, Flag } from 'lucide-react';
import { heroImages, galleryImages } from '../mock';

export default function About() {
  const values = [
    { icon: Users, title: 'Teamwork', desc: 'Together we win. We celebrate every scholar’s contribution to the team.' },
    { icon: ShieldCheck, title: 'Discipline', desc: 'On and off the field. Structure builds champions.' },
    { icon: Heart, title: 'Respect', desc: 'For coaches, teammates, opponents, and self. Always.' },
    { icon: Target, title: 'Work Ethic', desc: 'Show up. Give your best. Repeat. That’s the Patriot way.' }
  ];

  return (
    <div className="font-body">
      {/* Header */}
      <section className="relative pt-40 pb-24 bg-[#0a1f44] overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImages[1]} alt="stadium" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a1f44]/70 to-[#0a1f44]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Since 2004</div>
          <h1 className="font-display text-white text-6xl md:text-8xl leading-none">ABOUT US</h1>
          <p className="text-white/70 max-w-2xl mt-6 text-lg">
            The story of the &quot;ICE&quot; Brookline JP Patriots — 20 years of youth football, cheerleading, family, and championship pride.
          </p>
        </div>
      </section>

      <div className="stripe-sep" />

      {/* Passion */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 gap-14 items-center">
          <div className="relative">
            <div className="absolute -top-4 -right-4 w-full h-full border-4 border-[#c8102e]" />
            <img src={galleryImages[0]} alt="passion" className="relative w-full h-[320px] sm:h-[500px] object-cover" loading="lazy" decoding="async" />
          </div>
          <div>
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Our Passion</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl leading-tight mb-6">A CHAMPIONSHIP<br />PROGRAM. A FAMILY<br />FOR LIFE.</h2>
            <div className="space-y-4 text-[#0a1f44]/80 leading-relaxed">
              <p>The &quot;ICE&quot; Brookline JP Patriots has meant a lot to us — the countless memories shared over the years with kids and staff are something we will never forget.</p>
              <p>Watching this organization grow into a championship winning program in both football and cheer means the world to us. We are proud of one of our greatest achievements: seeing young scholars grow into respectable young adults.</p>
              <p>Teaching teamwork, discipline, respect and work ethic is the driving force behind our program — the reason we&apos;ve been successful over our 20 years of existence.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-24 bg-[#0a1f44] relative overflow-hidden">
        <div className="absolute inset-0 stars-bg" />
        <div className="relative max-w-4xl mx-auto px-6 text-center">
          <Flag className="w-12 h-12 text-[#c8102e] mx-auto mb-6" />
          <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Our Mission</div>
          <h2 className="font-display text-white text-4xl md:text-5xl leading-tight">
            &ldquo;THE &ldquo;ICE&rdquo; BROOKLINE JAMAICA PLAIN PATRIOTS ARE COMMITTED TO EMPOWERING YOUTH THROUGH <span className="text-[#c8102e]">TEAMWORK</span>, <span className="text-[#c8102e]">DISCIPLINE</span>, AND <span className="text-[#c8102e]">LEADERSHIP</span> WHILE FOSTERING A STRONG COMMUNITY CONNECTION.&rdquo;
          </h2>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Core Values</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">WHAT WE STAND FOR</h2>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {values.map((v) => (
              <div key={v.title} className="hover-lift bg-white border-2 border-[#0a1f44]/10 p-8 group hover:border-[#c8102e]">
                <div className="w-14 h-14 bg-[#0a1f44] group-hover:bg-[#c8102e] flex items-center justify-center mb-5 transition-colors">
                  <v.icon className="w-6 h-6 text-white" />
                </div>
                <div className="font-display text-2xl text-[#0a1f44] mb-2 tracking-wide">{v.title.toUpperCase()}</div>
                <p className="text-[#0a1f44]/70 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-24 bg-[#f5f6f8]">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Our History</div>
            <h2 className="font-display text-[#0a1f44] text-5xl md:text-6xl">20 YEARS STRONG</h2>
          </div>
          <div className="relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-0.5 bg-[#c8102e]" />
            {[
              { y: '2004', t: 'Program Founded', d: 'Brookline JP Patriots is born from a shared vision to serve JP youth.' },
              { y: '2007', t: 'Football — MVL Co-Champions & Eastern Mass. Runner-Up', d: 'Merrimack Valley League Co-Champions: Pee Wee "C" Team Division 2 AND JR Pee Wee "D" Division. Eastern Mass. Runner-Up — JR Pee Wee "D" Team Division 2.' },
              { y: '2008', t: 'Football — Pop Warner Eastern Mass. Double Champions', d: 'JR Pee Wee "D" Team Division 2 Champions AND Pee Wee "C" Team Division 2 Champions (Pop Warner Conference of Eastern Mass. Division 2). JR. Pee Wee Division II Consolation Runner-Up at the Pop Warner Super Bowl (Disney\'s Wide World of Sports).' },
              { y: '2009', t: 'Football — Pop Warner Eastern Mass. & MVL Champions', d: 'Pee Wee "C" Team Division 1 Champion (Pop Warner Conf. of Eastern Mass. / Eastern Mass. Champions). MVL JR Pee Wee "D" Division Champions and Midget "A" Division — Brookline JP Patriots.' },
              { y: '2010', t: 'Football — Pop Warner Eastern Mass. Division 1 Champions', d: 'PWFC of Eastern Mass. Division 1 "A" Team Champions AND "D" Team Champions. Merrimack Valley League JR Pee Wee "D" Division Co-Champions.' },
              { y: '2011', t: 'Football — New England Regional Champions', d: 'JR Midget — New England Region Division 1 Champion. PWFC of Eastern Mass. Division 1 "B" Team Champions. MVL Midget "A" Division 1 Champions and JR Midget "B" Division 1 Co-Champions. PWFC of Eastern Mass. Midget "A" Runner-Up Division 1. JR. Midget Division I Semi-Finalist at the Pop Warner Super Bowl.' },
              { y: '2012', t: 'Football — Pop Warner Eastern Mass. "A" & "C" Champions', d: 'PWFC of Eastern Mass. Division 1 "A" Team Champions AND "C" Team Champions Division 1.' },
              { y: '2013', t: 'Football — New England Regional Division 1 Champions', d: '"Midget" squad wins the New England Region Division 1 Football Championship. MVL Midget "A" Division 1 Champions — Brookline JP Patriots. Pee Wee Division I Semi-Finalist at the Pop Warner Super Bowl (Super Bowl LVII, Disney\'s Wide World of Sports).' },
              { y: '2014', t: 'Cheer Runner-Up & Football MVL Co-Champions', d: 'Cheer — Pop Warner Pee Wee Division 3 Runner-Up (New England Region). Football — MVL Pee Wee "C" Division 1 Co-Champions.' },
              { y: '2021', t: 'Football — National League U8 Division 2 Champions', d: 'Our U8 football squad captures the National League Division 2 Championship. Brookline JP Patriots Football Champions 2021.' },
              { y: '2023', t: 'Cheer — New England Region First Place', d: 'Pop Warner Cheer & Dance Competition — Varsity, Small Show Cheer 1: First Place.' },
              { y: '2024', t: 'Cheer & Dance National Champions', d: 'Pop Warner Cheer & 37th Dance Nationals — 2024 Dance Globals NATIONAL CHAMPION. Our cheer & dance squads take home top honors on the national stage.' },
              { y: '2025', t: 'Football — 7U Division 2 Champions', d: 'Northeastern MA, 7U Division 2 Football Champions. Pop Warner Little Scholars — championship season for our youngest football squad.' }
            ].map((it, i) => (
              <div key={it.y} className={`relative flex md:items-center gap-6 mb-10 ${i % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'}`}>
                <div className="absolute left-4 md:left-1/2 w-3 h-3 -translate-x-1/2 mt-2 bg-white border-2 border-[#c8102e] rotate-45" />
                <div className="pl-12 md:pl-0 md:w-1/2 md:px-8">
                  <div className="bg-white p-6 border-l-4 border-[#c8102e] shadow-sm">
                    <div className="font-display text-3xl text-[#c8102e]">{it.y}</div>
                    <div className="font-heading uppercase tracking-widest text-[#0a1f44] mt-1">{it.t}</div>
                    <p className="text-[#0a1f44]/70 text-sm mt-2">{it.d}</p>
                  </div>
                </div>
                <div className="hidden md:block md:w-1/2" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Super Bowl Plaques — Wall of Championship Runs */}
      <section className="py-20 bg-[#0a1f44]" data-testid="superbowl-plaques">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <Award className="w-10 h-10 text-[#c8102e] mx-auto mb-4" />
            <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-xs mb-4">Pop Warner Super Bowl</div>
            <h2 className="font-display text-white text-5xl md:text-6xl">DISNEY'S WIDE WORLD OF SPORTS</h2>
            <p className="text-white/70 mt-4">Three Patriots squads representing Brookline Jamaica Plain on the biggest stage in youth football.</p>
          </div>
          <div className="grid grid-cols-3 gap-3 md:gap-8">
            {[
              { y: '2008', label: 'JR. Pee Wee Division II Consolation Runner-Up', img: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_navy-red-hub/artifacts/fqoorcy8_2008%20superbowl.webp' },
              { y: '2011', label: 'JR. Midget Division I Semi-Finalist', img: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_navy-red-hub/artifacts/j98zrbi6_2011%20superbowl.webp' },
              { y: '2013', label: 'Pee Wee Division I Semi-Finalist', img: 'https://customer-assets-gfyr7b9c.emergentagent.net/job_navy-red-hub/artifacts/5et0mh9h_2013%20superbowl.webp' }
            ].map((p) => (
              <div key={p.y} data-testid={`superbowl-plaque-${p.y}`} className="group relative bg-white p-3 shadow-2xl shadow-black/40 hover:-translate-y-1 transition-transform duration-500">
                {/* Gold championship frame */}
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400/40 via-transparent to-amber-400/40 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
                <div className="relative aspect-[4/3] bg-[#0a1f44]/5 overflow-hidden">
                  <img
                    src={p.img}
                    alt={`${p.y} Pop Warner Super Bowl — ${p.label}`}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                    decoding="async"
                    style={{
                      imageRendering: '-webkit-optimize-contrast',
                      filter: 'contrast(1.08) saturate(1.15) brightness(1.02)'
                    }}
                  />
                </div>
                <div className="mt-4 text-center border-t border-[#0a1f44]/10 pt-4">
                  <div className="font-heading uppercase tracking-[0.3em] text-[#c8102e] text-[10px]">Pop Warner Super Bowl</div>
                  <div className="font-display text-4xl text-[#0a1f44] mt-1">{p.y}</div>
                  <div className="font-heading uppercase tracking-widest text-[10px] text-[#0a1f44]/70 mt-2 leading-relaxed">{p.label}</div>
                  <div className="mt-3 inline-flex items-center gap-1 text-amber-600 font-heading uppercase tracking-widest text-[9px]">
                    <Award className="w-3 h-3 fill-current" /> Disney · ESPN Wide World of Sports
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { n: '12', l: 'Championships' },
              { n: '500+', l: 'Alumni' },
              { n: '20', l: 'Years' },
              { n: '6', l: 'Divisions' }
            ].map((s) => (
              <div key={s.l} className="text-center border-2 border-[#0a1f44]/10 p-8 hover-lift">
                <Award className="w-8 h-8 text-[#c8102e] mx-auto mb-3" />
                <div className="font-display text-5xl text-[#0a1f44]">{s.n}</div>
                <div className="font-heading uppercase tracking-widest text-xs text-[#0a1f44]/60 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
